/**
 * Dark Mode Toggle Component
 * Complete dark theme implementation with system preference detection
 */

import React, { useState, useEffect } from 'react';
import { Sun, Moon, Desktop } from '@phosphor-icons/react';
import { glassOutlineClass } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';

interface DarkModeToggleProps {
  className?: string;
  showLabel?: boolean;
}

type Theme = 'light' | 'dark' | 'system';

const themeConfig: Record<Theme, { icon: React.ElementType; label: string; next: Theme }> = {
  light: { icon: Sun, label: 'Light', next: 'dark' },
  dark: { icon: Moon, label: 'Dark', next: 'system' },
  system: { icon: Desktop, label: 'System', next: 'light' },
};

export const DarkModeToggle: React.FC<DarkModeToggleProps> = ({ 
  className = '', 
  showLabel = true 
}) => {
  const getInitialTheme = (): Theme => {
    if (typeof window === 'undefined') return 'system';
    const saved = localStorage.getItem('theme') as Theme;
    return (saved && saved in themeConfig) ? saved : 'system';
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem('theme') as Theme;
    if (saved && saved in themeConfig) {
      setTheme(saved);
      const root = document.documentElement;
      const effective = saved === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : saved;
      if (!root.classList.contains(effective)) {
        root.classList.remove('light', 'dark');
        root.classList.add(effective);
      }
    }
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;
    const applyTheme = (t: 'light' | 'dark') => {
      root.classList.remove('light', 'dark');
      root.classList.add(t);
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', t === 'dark' ? '#020617' : '#ffffff');
    };

    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mq.matches ? 'dark' : 'light');
      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else {
      applyTheme(theme);
    }
  }, [theme, mounted]);

  const handleToggle = () => {
    const next = themeConfig[theme].next;
    setTheme(next);
    localStorage.setItem('theme', next);
  };

  const { icon: Icon, label } = themeConfig[mounted ? theme : 'system'];

  return (
    <div className={`dark-mode-toggle ${className}`}>
      <button
        onClick={handleToggle}
        disabled={!mounted}
        className={cn(
          glassOutlineClass,
          'flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-xs font-semibold',
        )}
        title={`Current: ${label}. Click for ${themeConfig[mounted ? theme : 'system'].next}.`}
      >
        <Icon size={16} weight="bold" />
        {showLabel && <span>{label}</span>}
      </button>
    </div>
  );
};

export default DarkModeToggle;